import React, { useState } from "react";
import API from "services/axios";

/**
 * Product Details / Product Images / Product Size Chart, at the end of a
 * product card's description row on the customer approval page.
 *
 * The three values come from the product catalogue (Sales > Utilities >
 * Products > Product Information) and reach this page on
 * `WOProducts[].productInformation`, matched on styleId by getApprovalDetails.
 *
 * A button is rendered ONLY when its value exists. A customer cannot tell a
 * disabled button from a broken one, and most products will have none of the
 * three set for a long while yet -- three dead buttons on every card would
 * read as a broken page.
 *
 * The PDFs are stored as S3 keys, so opening one costs a round trip to swap
 * the key for a signed link. /aws/images is public, which is what lets this
 * work for a customer who is not logged in.
 */

// A url typed without a scheme would resolve against this app's own origin.
export const toHref = (url) => {
  const value = String(url || "").trim();
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

export default function ProductInformationButtons({ information, onError }) {
  const [busy, setBusy] = useState(null);

  const url = information?.productUrl;
  const imagePdf = information?.productImagePdf;
  const chartPdf = information?.productSizeChartPdf;

  if (!url && !imagePdf && !chartPdf) return null;

  const openStoredFile = async (key, which) => {
    try {
      setBusy(which);
      // inline + the type: objects uploaded before the bucket was told what
      // they are come back as application/octet-stream, which a browser
      // downloads. This asks for a link that DISPLAYS the PDF in the new tab.
      const { data } = await API.post("/aws/images", {
        key,
        inline: true,
        contentType: "application/pdf",
      });
      const link = Array.isArray(data) ? data[0] : data;
      if (!link) throw new Error("That file could not be opened.");
      window.open(link, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (onError) onError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(null);
    }
  };

  const pdfButton = (key, which, label, icon) => (
    <button
      type="button"
      className="btn btn-sm btn-warning d-inline-flex align-items-center gap-1"
      disabled={busy === which}
      onClick={() => openStoredFile(key, which)}
      // The tooltip LEADS with the label: a title can win over the visible
      // text as the accessible name, and a button announced only as "Open
      // the size chart (PDF)" no longer matches what is on screen.
      title={`${label} — opens the PDF`}
    >
      <i className={`mdi ${busy === which ? "mdi-loading mdi-spin" : icon}`}></i>
      <span>{label}</span>
    </button>
  );

  return (
    <div className="product-information-buttons d-inline-flex align-items-center gap-1 flex-wrap">
      {url && (
        <a
          href={toHref(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-warning d-inline-flex align-items-center gap-1"
          title="Product Details — opens the product page"
        >
          <i className="mdi mdi-link-variant"></i>
          <span>Product Details</span>
        </a>
      )}
      {imagePdf &&
        pdfButton(imagePdf, "image", "Product Images", "mdi-file-image-outline")}
      {chartPdf &&
        pdfButton(chartPdf, "chart", "Product Size Chart", "mdi-ruler-square")}
    </div>
  );
}
